import React, { useEffect, useRef } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';

export default function Preview({ html, filename, setResult }) {
    const ref = useRef(null);

    useEffect(() => {
        if (!html) return;
        const typeset = () => {
            if (window.MathJax && window.MathJax.typesetPromise) {
                window.MathJax.typesetPromise([ref.current]).catch((e) => console.error(e));
            }
            // (Опционально) WIRIS:
            // if (window.com && window.com.wiris && window.com.wiris.jsApi) {
            //   window.com.wiris.jsApi.mathml2img(ref.current);
            // }
        };
        const id = setTimeout(typeset, 0);
        return () => clearTimeout(id);
    }, [html]);

    const download = () => {
        const blob = new Blob([html.replace('<noscript>', '<style>').replace('</noscript>', '</style>')], { type: 'text/html;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const nameBase = (filename || 'document').replace(/\.[^/.]+$/, '');
        a.download = `${nameBase}.html`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    // Список блочных тегов, на которые корректно вешать text-align
    const BLOCK_TAGS = new Set([
        'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
        'LI', 'UL', 'OL', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH',
        'BLOCKQUOTE', 'SECTION', 'ARTICLE', 'ASIDE', 'HEADER', 'FOOTER', 'NAV',
        'FIGURE', 'FIGCAPTION', 'MAIN', 'IMG'
    ]);

    const isInsidePreview = (node) => {
        if (!node || !ref.current) return false;
        return ref.current.contains(node.nodeType === Node.ELEMENT_NODE ? node : node.parentNode);
    };

    const getNearestBlockAncestor = (node) => {
        let el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
        while (el && el !== ref.current) {
            if (BLOCK_TAGS.has(el.tagName)) return el;
            el = el.parentElement;
        }
        return null;
    };

    const collectBlocksFromSelection = (selection) => {
        const blocks = new Set();

        if (!selection || selection.rangeCount === 0) return blocks;

        // Если просто каретка (нет выделения) — работаем с ближайшим блочным контейнером
        if (selection.isCollapsed) {
            const block = getNearestBlockAncestor(selection.anchorNode);
            if (block && isInsidePreview(block)) blocks.add(block);
            return blocks;
        }

        const range = selection.getRangeAt(0);

        // Защита: если выделение вне превью, ничего не делаем
        if (!isInsidePreview(range.commonAncestorContainer)) return blocks;

        // Обходим текстовые узлы в пределах превью и проверяем, попадают ли они в выделение
        const walker = document.createTreeWalker(
            ref.current,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: (node) => {
                    // Пропускаем пустые/пробельные
                    if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
                    return selection.containsNode(node, true)
                        ? NodeFilter.FILTER_ACCEPT
                        : NodeFilter.FILTER_REJECT;
                }
            }
        );

        let textNode;
        while ((textNode = walker.nextNode())) {
            const block = getNearestBlockAncestor(textNode);
            if (block && isInsidePreview(block)) {
                blocks.add(block);
            }
        }


        const edgeBlocks = [
            getNearestBlockAncestor(range.startContainer),
            getNearestBlockAncestor(range.endContainer)
        ];
        edgeBlocks.forEach(b => { if (b && isInsidePreview(b)) blocks.add(b); });


        if (blocks.size === 0) {
            const commonBlock = getNearestBlockAncestor(range.commonAncestorContainer);
            if (commonBlock && isInsidePreview(commonBlock)) blocks.add(commonBlock);
        }

        return blocks;
    };

    const applyAlignment = (align) => {
        if (!ref.current) return;

        const selection = window.getSelection();
        if (!selection) return;

        const blocks = collectBlocksFromSelection(selection);
        if (blocks.size === 0) return;

        // Применяем выравнивание
        blocks.forEach((el) => {
            // Если align === 'left', можно убирать стиль, если хотите дефолт
            // Но оставим явное указание:
            el.style.textAlign = align;
        });

        // Сериализуем изменения в глобальный html
        const updatedHtml = ref.current.innerHTML;
        html = updatedHtml
        setResult(updatedHtml);
    };

    return (
        <div>
            <div className="d-flex justify-content-between mb-2">
                <ButtonGroup aria-label="alignment">
                    <Button size="sm" variant="outline-secondary" onClick={() => applyAlignment('left')}>
                        Выравнивание влево
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => applyAlignment('center')}>
                        По центру
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => applyAlignment('right')}>
                        Вправо
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => applyAlignment('justify')}>
                        По ширине
                    </Button>
                </ButtonGroup>
                <Button size="sm" onClick={download}>Скачать HTML</Button>
            </div>
            <div
                className="preview-frame"
                ref={ref}
                // Если хотите позволять редактирование внутри превью:
                // contentEditable
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
}